/**
 * ToolbarActions — right-side toolbar buttons: Create + global custom actions.
 *
 * - "Crear" always visible and enabled; shows warning toast when canCreate=false (NEVER-HIDE)
 * - globalActions render custom action buttons after "Crear"
 * - Uses native styled buttons to guarantee mock-matching appearance
 *
 * @module components/toolbar/ToolbarActions
 * @see FMPV-MVWC-E002-S002 I-002
 */
import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, DocumentArrowDownIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../Button/Button';
import type { MasterPatternViewDefinition } from '../../types/definition.types';
import type { MasterPatternViewPermissions } from '../../types/permissions.types';
import type { MasterPatternViewCustomAction, MasterPatternViewDefaultActions } from '../../types/props.types';
import type { MasterPatternViewEntity } from '../../types/service.types';
import { isActionAllowed, getDisabledToastMessage } from '../../engine/permissionEngine';
import { useToast, type ToastCallback } from '../../hooks/useToast';

export interface ToolbarActionsProps<T extends MasterPatternViewEntity = MasterPatternViewEntity> {
  definition: MasterPatternViewDefinition;
  permissions?: MasterPatternViewPermissions;
  defaultActions?: MasterPatternViewDefaultActions<T>;
  onCreateClick: () => void;
  globalActions?: MasterPatternViewCustomAction<T>[];
  selectedRecords?: T[];
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  showExport?: boolean;
  showImport?: boolean;
  onExport?: () => void;
  onImport?: () => void;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}


export function ToolbarActions<T extends MasterPatternViewEntity = MasterPatternViewEntity>({
  definition,
  permissions,
  defaultActions,
  onCreateClick,
  globalActions = [],
  selectedRecords = [],
  toast: toastProp,
  showInternalToasts = true,
  showExport = false,
  showImport = false,
  onExport,
  onImport,
  t,
}: ToolbarActionsProps<T>) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  const { showWarning } = useToast(toastProp, showInternalToasts);
  const canCreate = isActionAllowed(permissions, 'CREATE', definition.type);

  const handleCreateClick = useCallback(() => {
    if (!canCreate) {
      showWarning(getDisabledToastMessage('CREATE'));
      return;
    }
    onCreateClick();
  }, [canCreate, showWarning, onCreateClick]);

  return (
    <div className="flex items-center gap-2" data-testid="toolbar-actions">
      {/* "Crear" — hidden only when defaultActions.create.show === false; otherwise NEVER-HIDE */}
      {defaultActions?.create?.show !== false && (
        <motion.span
          className="inline-block"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', bounce: 0.35, duration: 0.35 }}
        >
          <Button
            type="default"
            size="base"
            className="!text-sm font-medium"
            leftIcon={<PlusIcon className="w-4 h-4" />}
            onClick={handleCreateClick}
            data-testid="toolbar-create-button"
          >
            {tt('create', 'Nuevo')}
          </Button>
        </motion.span>
      )}

      {/* Export button */}
      {showExport && (
        <Button
          type="outline"
          size="sm"
          leftIcon={<DocumentArrowDownIcon className="w-4 h-4" />}
          onClick={onExport}
          data-testid="toolbar-export-button"
        >
          {tt('export', 'Exportar')}
        </Button>
      )}

      {/* Import button */}
      {showImport && (
        <Button
          type="outline"
          size="sm"
          leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
          onClick={onImport}
          data-testid="toolbar-import-button"
        >
          {tt('import', 'Importar')}
        </Button>
      )}

      {/* Custom global actions */}
      {globalActions.map((action) => {
        const isDisabled = action.isDisabled?.() ?? false;
        return (
          <Button
            key={action.key}
            type="default"
            size="sm"
            leftIcon={action.icon}
            onClick={() => !isDisabled && action.onExecute()}
            disabled={isDisabled}
            data-testid={`global-action-${action.key}`}
          >
            {action.label}
          </Button>
        );
      })}
    </div>
  );
}
